import { MetricsBox, Panel, tokens } from '@cast/design-system';
import type { ServiceHealthMetricView } from '@cast/features-cluster-shared';
import { isEmptyMetricValue } from '@cast/features-cluster-shared-utils';

type ServiceHealthTileProps = {
  metric: ServiceHealthMetricView;
};

/** Matches Figma SLO panel states: default | disabled | error | warning */
type TileVisualState = 'default' | 'disabled' | 'error' | 'warning';

const TILE_STYLES: Record<
  TileVisualState,
  {
    bgcolor: string;
    borderColor: string;
    labelColor: string;
    valueColor: string;
  }
> = {
  default: {
    bgcolor: tokens.theme.bg.structural.surface,
    borderColor: tokens.theme.border.structural.neutral,
    labelColor: tokens.theme.fg.structural.secondary,
    valueColor: tokens.theme.fg.structural.primary,
  },
  disabled: {
    bgcolor: tokens.theme.bg.structural.surface,
    borderColor: tokens.theme.disabled.border,
    labelColor: tokens.theme.disabled.fg,
    valueColor: tokens.theme.disabled.fg,
  },
  error: {
    bgcolor: tokens.theme.bg.status.errorSubtle,
    borderColor: tokens.theme.border.status.errorSubtle,
    labelColor: tokens.theme.fg.structural.secondary,
    valueColor: tokens.theme.fg.status.error,
  },
  warning: {
    bgcolor: tokens.theme.bg.status.warningSubtle,
    borderColor: tokens.theme.border.status.warningSubtle,
    labelColor: tokens.theme.fg.structural.secondary,
    valueColor: tokens.theme.fg.status.warning,
  },
};

const getTileVisualState = (
  metric: ServiceHealthMetricView
): TileVisualState => {
  if (metric.status === 'breached') {
    return 'error';
  }

  if (metric.status === 'at_risk') {
    return 'warning';
  }

  if (metric.status === 'locked' || isEmptyMetricValue(metric.value)) {
    return 'disabled';
  }

  return 'default';
};

export const ServiceHealthTile = ({ metric }: ServiceHealthTileProps) => {
  const visualState = getTileVisualState(metric);
  const styles = TILE_STYLES[visualState];
  const isDisabled = visualState === 'disabled';

  return (
    <Panel
      sx={{
        p: 16,
        minWidth: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: styles.bgcolor,
        border: '1px solid',
        borderColor: styles.borderColor,
      }}
    >
      <MetricsBox
        size="m"
        slots={{
          label: metric.label,
          infoTooltip: isDisabled ? undefined : metric.infoTooltip,
          value: metric.value,
          unit: isDisabled ? undefined : metric.unit,
        }}
        slotProps={{
          root: { gap: 0 },
          label: {
            sx: {
              textTransform: 'uppercase',
              letterSpacing: '1px',
              color: styles.labelColor,
              lineHeight: '18px',
            },
          },
          value: {
            variant: 'metricmd',
            sx: {
              color: styles.valueColor,
              lineHeight: '28px',
              whiteSpace: 'nowrap',
            },
          },
          unit: {
            variant: 'L12R',
            sx: {
              textTransform: 'uppercase',
              letterSpacing: '1px',
              color: tokens.theme.fg.structural.secondary,
              lineHeight: '18px',
              pb: '1px',
            },
          },
        }}
      />
    </Panel>
  );
};
